import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  questionInputSchema,
  skillLabel,
  type QuestionInput,
  type QuestionType,
} from '@ready/shared';
import { useApi, type ContentOwner, type Question } from '@/lib/api';
import { Button, ErrorText, Field, Input, Select, Textarea } from '@/components/ui';
import {
  AiPanel,
  DraftingNote,
  SourceFields,
  emptySource,
  sourceInput,
  useAiDraft,
  type SourceValue,
} from './ai-assist';

const TYPES: { type: QuestionType; label: string }[] = [
  { type: 'single', label: 'One answer' },
  { type: 'multiple', label: 'Several answers' },
  { type: 'true_false', label: 'True or false' },
];

const NEW_BANK = '__new__';

/**
 * Drafts questions from a topic, a lesson or source material. The author
 * reviews each one (edits text, fixes answers, drops what they don't want)
 * and saves the rest into a question bank in one go.
 */
export function QuestionAssist({
  owner,
  title = 'Draft questions with AI',
  bankId: fixedBankId,
  lessonId,
  defaultBankTitle,
  defaultCount = 5,
  defaultTypes = ['single'],
  saveLabel = 'Save',
  onSaved,
  onClose,
}: {
  owner: ContentOwner;
  title?: string;
  /** Save into this bank; otherwise the author picks one or makes a new one. */
  bankId?: string;
  /** Draft from this lesson's saved text. */
  lessonId?: string;
  defaultBankTitle?: string;
  defaultCount?: number;
  defaultTypes?: QuestionType[];
  saveLabel?: string;
  onSaved: (questions: Question[], bank: { id: string; title: string }) => void;
  onClose: () => void;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [topic, setTopic] = useState('');
  const [count, setCount] = useState(defaultCount);
  const [types, setTypes] = useState<QuestionType[]>(defaultTypes);
  const [difficulty, setDifficulty] = useState('');
  const [skill, setSkill] = useState('');
  const [source, setSource] = useState<SourceValue>(emptySource);
  const ai = useAiDraft('questions');
  // Edited copies of the drafted questions, and which ones to keep.
  const [edited, setEdited] = useState<QuestionInput[] | null>(null);
  const [keep, setKeep] = useState<boolean[]>([]);
  const questions = edited ?? ai.draft?.questions ?? null;
  if (ai.draft && !edited) {
    setEdited(ai.draft.questions);
    setKeep(ai.draft.questions.map(() => true));
  }

  const banks = useQuery({
    queryKey: ['banks', owner.kind === 'org' ? owner.orgId : 'library'],
    queryFn: () => api.banks(owner),
    enabled: !fixedBankId,
  });
  const editableBanks = (banks.data?.banks ?? []).filter((b) => b.editable);
  const [bankChoice, setBankChoice] = useState('');
  const [newBankTitle, setNewBankTitle] = useState(defaultBankTitle ?? '');
  // Default: a bank already named like the suggested title, else a new one with that title.
  const suggested = defaultBankTitle
    ? (editableBanks.find((b) => b.title === defaultBankTitle)?.id ?? NEW_BANK)
    : (editableBanks[0]?.id ?? NEW_BANK);
  const chosenBank = fixedBankId ?? (bankChoice || suggested);

  const kept = (questions ?? []).filter((_, i) => keep[i]);
  const problems = (questions ?? []).map((q, i) =>
    keep[i] ? (questionInputSchema.safeParse(q).error?.issues[0]?.message ?? null) : null,
  );

  const save = useMutation({
    mutationFn: async () => {
      const bank =
        chosenBank === NEW_BANK
          ? await api.createBank(owner, { title: newBankTitle.trim() })
          : {
              id: chosenBank,
              title: editableBanks.find((b) => b.id === chosenBank)?.title ?? '',
            };
      const res = await api.createQuestions(
        bank.id,
        kept.map((q) => questionInputSchema.parse(q)),
      );
      return { questions: res.questions, bank: { id: bank.id, title: bank.title } };
    },
    onSuccess: ({ questions: saved, bank }) => {
      void queryClient.invalidateQueries({ queryKey: ['banks'] });
      void queryClient.invalidateQueries({ queryKey: ['bank', bank.id] });
      onSaved(saved, bank);
      onClose();
    },
  });

  const editQuestion = (i: number, patch: Partial<QuestionInput>) =>
    setEdited((all) => all!.map((q, j) => (j === i ? { ...q, ...patch } : q)));

  return (
    <AiPanel title={title} onClose={onClose}>
      {!questions && (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            ai.start({
              kind: 'questions',
              ownerOrgId: owner.kind === 'org' ? owner.orgId : null,
              count,
              types,
              ...(lessonId ? { lessonId } : {}),
              ...(topic.trim() ? { topic: topic.trim() } : {}),
              ...(difficulty ? { difficulty: Number(difficulty) } : {}),
              ...(skill ? { skill } : {}),
              ...sourceInput(source),
            });
          }}
        >
          {lessonId && (
            <p className="text-sm text-muted-foreground">
              Questions are drafted from the lesson's saved text.
            </p>
          )}
          <Field
            label={lessonId ? 'Focus (optional)' : 'Topic'}
            htmlFor="ai-topic"
            hint={
              lessonId
                ? 'Anything to emphasize or avoid.'
                : 'What the questions should test, and for whom.'
            }
          >
            <Textarea
              id="ai-topic"
              rows={3}
              maxLength={4000}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
          </Field>
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium">Question types</legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {TYPES.map((t) => (
                <label key={t.type} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-5 accent-primary"
                    checked={types.includes(t.type)}
                    onChange={() =>
                      setTypes((all) =>
                        all.includes(t.type)
                          ? all.length > 1
                            ? all.filter((x) => x !== t.type)
                            : all
                          : [...all, t.type],
                      )
                    }
                  />
                  {t.label}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            <Field label="How many" htmlFor="ai-count">
              <Select
                id="ai-count"
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20].map((n) => (
                  <option key={n}>{n}</option>
                ))}
              </Select>
            </Field>
            <Field label="Difficulty" htmlFor="ai-difficulty">
              <Select
                id="ai-difficulty"
                value={difficulty}
                onChange={(e) => setDifficulty(e.target.value)}
              >
                <option value="">Mixed</option>
                <option value="1">Easy</option>
                <option value="2">Medium</option>
                <option value="3">Hard</option>
              </Select>
            </Field>
            <Field label="Skill (optional)" htmlFor="ai-skill">
              <Input
                id="ai-skill"
                placeholder="e.g. tool_use"
                pattern="[a-z][a-z0-9_]{1,39}"
                value={skill}
                onChange={(e) => setSkill(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              />
            </Field>
          </div>
          {!lessonId && (
            <SourceFields id="ai-questions" owner={owner} value={source} onChange={setSource} />
          )}
          <Button
            type="submit"
            disabled={
              ai.drafting ||
              (!lessonId &&
                !topic.trim() &&
                !source.sourceText.trim() &&
                !source.sourceAssetIds.length)
            }
          >
            {ai.drafting ? 'Drafting…' : 'Draft questions'}
          </Button>
          <DraftingNote active={ai.drafting} />
          <ErrorText error={ai.error} />
        </form>
      )}

      {questions && (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Check each question and its answer. Untick any you don't want.
          </p>
          <ol className="space-y-3">
            {questions.map((q, i) => (
              <li
                key={i}
                className={`space-y-3 rounded-lg border p-3 ${keep[i] ? 'border-border' : 'border-dashed border-border opacity-60'}`}
              >
                <label className="flex items-start gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    className="mt-0.5 size-5 shrink-0 accent-primary"
                    checked={keep[i] ?? false}
                    onChange={(e) =>
                      setKeep((k) => k.map((x, j) => (j === i ? e.target.checked : x)))
                    }
                  />
                  <span>
                    Question {i + 1}
                    <span className="block text-xs font-normal text-muted-foreground">
                      {TYPES.find((t) => t.type === q.type)?.label} · {skillLabel(q.skill)} ·{' '}
                      {['', 'Easy', 'Medium', 'Hard'][q.difficulty]}
                    </span>
                  </span>
                </label>
                {keep[i] && (
                  <>
                    <Textarea
                      aria-label={`Question ${i + 1} prompt`}
                      rows={3}
                      className="field-sizing-content"
                      value={q.prompt}
                      onChange={(e) => editQuestion(i, { prompt: e.target.value })}
                    />
                    <ul className="space-y-2">
                      {q.options.map((o, oi) => (
                        <li key={o.id} className="flex items-start gap-2">
                          <input
                            type={q.type === 'multiple' ? 'checkbox' : 'radio'}
                            name={`ai-q${i}`}
                            aria-label={`Option ${oi + 1} is correct`}
                            className="mt-2.5 size-5 shrink-0 accent-primary"
                            checked={o.correct}
                            onChange={(e) =>
                              editQuestion(i, {
                                options: q.options.map((x) =>
                                  q.type === 'multiple'
                                    ? x.id === o.id
                                      ? { ...x, correct: e.target.checked }
                                      : x
                                    : { ...x, correct: x.id === o.id },
                                ),
                              })
                            }
                          />
                          <Textarea
                            aria-label={`Option ${oi + 1}`}
                            rows={o.text.length > 40 ? 2 : 1}
                            className="field-sizing-content resize-none"
                            value={o.text}
                            readOnly={q.type === 'true_false'}
                            onChange={(e) =>
                              editQuestion(i, {
                                options: q.options.map((x) =>
                                  x.id === o.id ? { ...x, text: e.target.value } : x,
                                ),
                              })
                            }
                          />
                        </li>
                      ))}
                    </ul>
                    <Textarea
                      aria-label={`Question ${i + 1} explanation`}
                      rows={3}
                      className="field-sizing-content"
                      placeholder="Explanation"
                      value={q.explanation ?? ''}
                      onChange={(e) =>
                        editQuestion(i, { explanation: e.target.value || undefined })
                      }
                    />
                    {problems[i] && <p className="text-sm text-destructive">{problems[i]}</p>}
                  </>
                )}
              </li>
            ))}
          </ol>

          {!fixedBankId && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Save to question bank" htmlFor="ai-bank">
                <Select
                  id="ai-bank"
                  value={chosenBank}
                  onChange={(e) => setBankChoice(e.target.value)}
                >
                  {editableBanks.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.title}
                    </option>
                  ))}
                  <option value={NEW_BANK}>New question bank…</option>
                </Select>
              </Field>
              {chosenBank === NEW_BANK && (
                <Field label="New bank title" htmlFor="ai-bank-title">
                  <Input
                    id="ai-bank-title"
                    minLength={3}
                    maxLength={120}
                    value={newBankTitle}
                    onChange={(e) => setNewBankTitle(e.target.value)}
                  />
                </Field>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={
                save.isPending ||
                kept.length === 0 ||
                problems.some(Boolean) ||
                (chosenBank === NEW_BANK && newBankTitle.trim().length < 3)
              }
              onClick={() => save.mutate()}
            >
              <Plus className="size-4" aria-hidden />
              {saveLabel} {kept.length} question{kept.length === 1 ? '' : 's'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setEdited(null);
                setKeep([]);
                ai.reset();
              }}
            >
              Start over
            </Button>
          </div>
          <ErrorText error={save.error} />
        </div>
      )}
    </AiPanel>
  );
}
